import type { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import {
  Bell,
  CalendarDays,
  LogOut,
  Settings,
  TrendingUp,
  Users,
  Wifi,
  WifiOff,
  UserCheck,
  UserX,
  Building2,
  CheckCheck,
  Package,
  ChevronDown,
  Building,
  Sun,
  Moon,
  Building as BuildingIcon,
} from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import AvisoSuscripcion from "@/components/AvisoSuscripcion";
import { cn } from "@/lib/utils";
import { useGlobalKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { CommandPaletteTrigger } from "@/components/CommandPalette";
import { useMisLocales } from "@/hooks/useDatos";
import { useTheme } from "next-themes";

const enlaces = [
  { to: "/app/agenda", texto: "Agenda", Icono: CalendarDays, admin: false },
  { to: "/app/recordatorios", texto: "Recordar", Icono: Bell, admin: false },
  { to: "/app/clientes", texto: "Clientes", Icono: Users, admin: false },
  { to: "/app/resumen", texto: "Resumen", Icono: TrendingUp, admin: true },
  { to: "/app/ajustes", texto: "Ajustes", Icono: Settings, admin: true },
  { to: "/app/inventario", texto: "Inventario", Icono: Package, admin: true },
];

export default function Layout({ children }: { children: ReactNode }) {
  const {
    session,
    tenantId,
    rol,
    cargando,
    salir,
    esAdmin,
    setMostrarSelectorLocal,
  } = useAuth();
  const { data: misLocales = [] } = useMisLocales();
  const navigate = useNavigate();
  const location = useLocation();
  const { setTheme, resolvedTheme } = useTheme();

  // Global keyboard shortcuts
  useGlobalKeyboardShortcuts({
    nuevoTurno: () => navigate("/app/agenda?new=true"),
    irAgenda: () => navigate("/app/agenda"),
    irClientes: () => navigate("/app/clientes"),
    irAjustes: () => navigate("/app/ajustes"),
    toggleTema: () => {
      const html = document.documentElement;
      html.dataset.theme = html.dataset.theme === "dark" ? "light" : "dark";
    },
  });

  const handleNavClick = (to: string) => {
    console.log("[Layout] Navegando a:", to);
    navigate(to);
  };

  // Encontrar el local actual
  const localActual = misLocales.find((l) => l.tenant_id === tenantId);

  // Cambiar de local navegando al subdominio correspondiente
  const cambiarLocal = (slug: string) => {
    const hostname = window.location.hostname;
    const baseDomain = hostname.replace(/^[^.]+\./, ""); // quita subdominio actual
    const nuevoHost = `${slug}.${baseDomain}`;
    window.location.href = `https://${nuevoHost}${window.location.pathname}${window.location.search}`;
  };

  // Debug badge visual (esquina sup. der.) — quitar en prod
  const debugAuth = (
    <div className="fixed top-2 right-2 z-[9999] bg-background/95 backdrop-blur border rounded-lg p-2 text-[10px] font-mono shadow-lg pointer-events-none">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">
          DEBUG
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1 mt-1 text-xs">
        <div className="flex items-center gap-1">
          {session ? (
            <UserCheck className="h-3 w-3 text-green-500" />
          ) : (
            <UserX className="h-3 w-3 text-red-500" />
          )}
          <span>Session: {session ? "Sí" : "No"}</span>
        </div>
        <div className="flex items-center gap-1">
          {tenantId ? (
            <CheckCheck className="h-3 w-3 text-green-500" />
          ) : (
            <Building2 className="h-3 w-3 text-red-500" />
          )}
          <span>Tenant: {tenantId ? "Sí" : "No"}</span>
        </div>
        <div className="flex items-center gap-1 col-span-2">
          <span>Rol: {rol ?? "—"}</span>
        </div>
        <div className="flex items-center gap-1 col-span-2">
          {cargando ? (
            <WifiOff className="h-3 w-3 text-amber-500 animate-pulse" />
          ) : (
            <Wifi className="h-3 w-3 text-green-500" />
          )}
          <span>Cargando: {cargando ? "Sí" : "No"}</span>
        </div>
      </div>
    </div>
  );

  // Header con selector de local (solo si tiene más de 1)
  const header = misLocales.length > 1 && (
    <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex max-w-3xl items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <Building className="h-5 w-5 text-muted-foreground" />
          <SalonSelector
            localActual={localActual}
            misLocales={misLocales}
            tenantId={tenantId}
            onCambiar={cambiarLocal}
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {rol?.toUpperCase()}
        </span>
      </div>
    </header>
  );

  // Simple custom dropdown para selector de salón
  function SalonSelector({
    localActual,
    misLocales,
    tenantId,
    onCambiar,
  }: {
    localActual: { tenant_nombre: string } | undefined;
    misLocales: {
      tenant_id: string;
      tenant_nombre: string;
      tenant_slug: string;
    }[];
    tenantId: string | null;
    onCambiar: (slug: string) => void;
  }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          setOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
          aria-haspopup="true"
          aria-expanded={open}
        >
          {localActual?.tenant_nombre ?? "Salón"}
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-1 w-56 rounded-md border bg-popover p-1 shadow-lg z-50">
            <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Mis salones
            </p>
            <hr className="my-1 border-muted" />
            {misLocales.map((local) => (
              <button
                key={local.tenant_id}
                onClick={() => {
                  onCambiar(local.tenant_slug);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 text-sm rounded ${local.tenant_id === tenantId ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}
              >
                <span>{local.tenant_nombre}</span>
                {local.tenant_id === tenantId && (
                  <Building2 className="h-4 w-4 text-primary" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-3xl flex-col relative">
      {debugAuth}
      {header}
      <main className="flex-1 space-y-4 p-4 pb-24">
        <AvisoSuscripcion />
        {children}
      </main>
      <nav className="fixed inset-x-0 bottom-0 border-t bg-background z-50">
        <div className="mx-auto flex max-w-3xl">
          {enlaces
            .filter((e) => !e.admin || esAdmin)
            .map(({ to, texto, Icono }) => {
              const isActive = location.pathname === to;
              return (
                <button
                  key={to}
                  type="button"
                  onClick={() => handleNavClick(to)}
                  className={cn(
                    "flex flex-1 flex-col items-center gap-1 py-3 text-xs",
                    isActive
                      ? "font-medium text-primary"
                      : "text-muted-foreground",
                  )}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Icono className="h-5 w-5" />
                  {texto}
                </button>
              );
            })}
          {misLocales.length > 1 && (
            <button
              type="button"
              onClick={() => setMostrarSelectorLocal(true)}
              className="flex flex-1 flex-col items-center gap-1 py-3 text-xs text-muted-foreground"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              title="Cambiar de salón"
            >
              <BuildingIcon className="h-5 w-5" />
              Cambiar
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              setTheme(resolvedTheme === "dark" ? "light" : "dark")
            }
            className="flex flex-1 flex-col items-center gap-1 py-3 text-xs text-muted-foreground"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
            title={`Tema: ${resolvedTheme === "dark" ? "Oscuro" : "Claro"}`}
          >
            {resolvedTheme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
            Tema
          </button>
          <button
            type="button"
            onClick={salir}
            className="flex flex-1 flex-col items-center gap-1 py-3 text-xs text-muted-foreground"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            <LogOut className="h-5 w-5" />
            Salir
          </button>
        </div>
      </nav>
      <CommandPaletteTrigger />
    </div>
  );
}
